import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { api, fmtError } from "../api";
import { Mail, KeyRound, ArrowLeft, Loader2 } from "lucide-react";

// Zweistufige Anmeldung: 1) E-Mail -> 6-stelligen Code anfordern,
// 2) Code eingeben -> JWT erhalten und Session anlegen. Prefix "Test:" waehlt
// das Testsystem (wird vor dem Mail-Call serverseitig entfernt).
export default function Login() {
  const { magSession, loginMag } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // 'email' | 'code'
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches);

  useEffect(() => {
    if (magSession) navigate("/objekte", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const h = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);

  const trimmedEmail = email.trim();
  const cleanEmail = trimmedEmail.replace(/^test:/i, "").trim();
  const isTest = /^test:/i.test(trimmedEmail);

  const sendCode = async (e) => {
    e?.preventDefault();
    setBusy(true); setError(""); setCode("");
    try {
      await api.post("/magauth/auth/mail", { username: email });
      setStep("code");
    } catch (err) {
      setError(fmtError(err, "Code konnte nicht gesendet werden"));
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e?.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/magauth/auth/verify", { username: email, code });
      loginMag({
        token: data.token, mode: data.mode || "prod", root_permid: data.root_permid || "",
        user_name: data.user_name, user_id: data.user_id,
      });
      navigate("/objekte");
    } catch (err) {
      setError(fmtError(err, "Code ungültig oder abgelaufen"));
    } finally { setBusy(false); }
  };

  const reset = () => { setStep("email"); setCode(""); setError(""); };

  return (
    <>
      {isDesktop && <div aria-hidden data-testid="login-bg" style={{ position: "fixed", inset: 0, zIndex: 0, backgroundImage: "url('/login-bg.jpg')", backgroundSize: "cover", backgroundPosition: "center", filter: "blur(8px)", transform: "scale(1.08)" }} />}
      {isDesktop && <div aria-hidden style={{ position: "fixed", inset: 0, zIndex: 0, background: "rgba(248,250,252,0.62)" }} />}
    <div className="container-wd py-12 flex justify-center" data-testid="login-page" style={{ position: "relative", zIndex: 1 }}>
      <div className="card-wd p-8 w-full" style={{ maxWidth: 440 }}>
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: "#FFF7ED" }}>
          <KeyRound size={22} color="#F97316" />
        </div>
        <h1 className="text-2xl font-extrabold mb-1">Anmelden</h1>
        <p className="text-sm mb-6" style={{ color: "#64748B" }}>
          {step === "email"
            ? "Anmeldung per E-Mail-Code"
            : <>Wir haben einen Code an <strong>{cleanEmail}</strong> gesendet.</>}
        </p>

        {step === "email" ? (
          <form onSubmit={sendCode} className="flex flex-col gap-4" data-testid="login-email-form">
            <label className="field">
              <span className="field-label">E-Mail-Adresse</span>
              <div style={{ position: "relative" }}>
                <Mail size={16} style={{ position: "absolute", left: 12, top: 14, color: "#94A3B8" }} />
                <input className="input-wd" data-testid="login-email-input" required type="text" autoComplete="username"
                  style={{ paddingLeft: 38 }} placeholder="name@firma.de"
                  value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
            </label>
            {error && <p data-testid="login-error" className="text-sm font-semibold" style={{ color: "#DC2626" }}>{error}</p>}
            <button type="submit" className="btn-primary" data-testid="login-send-code" disabled={busy || !cleanEmail.includes("@")}>
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} {busy ? "Code wird gesendet…" : "Code senden"}
            </button>
          </form>
        ) : (
          <form onSubmit={verify} className="flex flex-col gap-4" data-testid="login-code-form">
            {isTest && (
              <span className="badge-hero self-start" data-testid="login-test-badge"
                style={{ background: "#EFF6FF", borderColor: "#BFDBFE", color: "#1D4ED8" }}>Testsystem</span>
            )}
            <label className="field">
              <span className="field-label">6-stelliger Code</span>
              <input className="input-wd" data-testid="login-code-input" required inputMode="numeric" autoFocus
                maxLength={6} placeholder="––––––"
                style={{ letterSpacing: "0.4em", fontSize: 20, textAlign: "center" }}
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} />
            </label>
            {error && <p data-testid="login-error" className="text-sm font-semibold" style={{ color: "#DC2626" }}>{error}</p>}
            <button type="submit" className="btn-primary" data-testid="login-verify" disabled={busy || code.length < 6}>
              {busy ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />} {busy ? "Anmelden…" : "Anmelden"}
            </button>
            <div className="flex items-center justify-between">
              <button type="button" data-testid="login-back" onClick={reset}
                className="text-sm font-semibold inline-flex items-center gap-1"
                style={{ background: "none", border: "none", cursor: "pointer", color: "#64748B" }}>
                <ArrowLeft size={14} /> E-Mail ändern
              </button>
              <button type="button" data-testid="login-resend" onClick={sendCode} disabled={busy}
                className="text-sm font-semibold inline-flex items-center gap-1"
                style={{ background: "none", border: "none", cursor: "pointer", color: "#F97316" }}>
                {busy ? <><Loader2 size={13} className="animate-spin" /> Senden…</> : "Code erneut senden"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
    </>
  );
}
